<template>
  <div class="match-summary">
    <div class="summary-header">
      <h1>Match Summary</h1>
      <div class="match-result" :class="isTeamMode ? winner.toLowerCase() : 'ffa'">
        <span class="winner-text" v-if="isTeamMode">{{ winner }} Team Wins!</span>
        <span class="winner-text" v-else>{{ winner }} Wins!</span>
      </div>
      <div class="match-info">
        <div class="match-scores" v-if="isTeamMode">
          <div class="team-score red">
            <span class="team-name">Red Team</span>
            <span class="score">{{ redTeamScore }}</span>
          </div>
          <span class="score-separator">:</span>
          <div class="team-score blue">
            <span class="team-name">Blue Team</span>
            <span class="score">{{ blueTeamScore }}</span>
          </div>
        </div>
        <div class="match-scores" v-else>
          <div class="ffa-winner">
            <span class="detail-label">Winner:</span>
            <span class="detail-value">{{ winner }}</span>
          </div>
        </div>
        <div class="match-details">
          <div class="detail">
            <span class="detail-label">Match ID:</span>
            <span class="detail-value">{{ matchId }}</span>
          </div>
          <div class="detail">
            <span class="detail-label">Map:</span>
            <span class="detail-value">{{ map }}</span>
          </div>
          <div class="detail">
            <span class="detail-label">Duration:</span>
            <span class="detail-value">{{ formatDuration(matchDuration) }}</span>
          </div>
        </div>
      </div>
    </div>
    
    <div class="stats-tabs">
      <button 
        class="tab-button" 
        :class="{ active: activeTab === 'scoreboard' }"
        @click="activeTab = 'scoreboard'"
      >
        Scoreboard
      </button>
      <button 
        class="tab-button" 
        :class="{ active: activeTab === 'details' }"
        @click="activeTab = 'details'"
      >
        Match Details
      </button>
      <button 
        class="tab-button" 
        :class="{ active: activeTab === 'awards' }"
        @click="activeTab = 'awards'"
      >
        Awards
      </button>
    </div>
    
    <div class="tab-content">
      <!-- Scoreboard Tab -->
      <div v-if="activeTab === 'scoreboard'" class="scoreboard-content">
        <table class="stats-table">
          <thead>
            <tr>
              <th class="rank">#</th>
              <th class="player-col">Player</th>
              <th v-if="isTeamMode">Team</th>
              <th>K</th>
              <th>D</th>
              <th>A</th>
              <th>KDR</th>
              <th>Score</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="(player, index) in sortedPlayers" 
              :key="player.id"
              :class="{ 'current-player': player.id === currentPlayerId }"
            >
              <td class="rank">{{ index + 1 }}</td>
              <td class="player-col">
                <span class="player-name">{{ player.username }}</span>
                <span class="player-character">({{ player.character }})</span>
              </td>
              <td v-if="isTeamMode" :class="['team-cell', player.team.toLowerCase()]">{{ player.team }}</td>
              <td>{{ player.kills }}</td>
              <td>{{ player.deaths }}</td>
              <td>{{ player.assists || 0 }}</td>
              <td>{{ calculateKDR(player) }}</td>
              <td class="score-cell">{{ calculateScore(player) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <!-- Match Details Tab -->
      <div v-if="activeTab === 'details'" class="details-content">
        <div class="match-stats">
          <div class="stat-card">
            <div class="stat-title">Total Kills</div>
            <div class="stat-value large">{{ totalKills }}</div>
            <div class="stat-breakdown" v-if="isTeamMode">
              <div class="team-stat red">
                <span class="team-name">Red:</span>
                <span class="team-value">{{ redTeamKills }}</span>
              </div>
              <div class="team-stat blue">
                <span class="team-name">Blue:</span>
                <span class="team-value">{{ blueTeamKills }}</span>
              </div>
            </div>
          </div>
          
          <div class="stat-card">
            <div class="stat-title">Average KDR</div>
            <div class="stat-value large">{{ averageKDR }}</div>
            <div class="stat-breakdown" v-if="isTeamMode">
              <div class="team-stat red">
                <span class="team-name">Red:</span>
                <span class="team-value">{{ redTeamKDR }}</span>
              </div>
              <div class="team-stat blue">
                <span class="team-name">Blue:</span>
                <span class="team-value">{{ blueTeamKDR }}</span>
              </div>
            </div>
          </div>
          
          <div class="stat-card">
            <div class="stat-title">Character Usage</div>
            <div class="character-usage">
              <div class="character-stat">
                <div class="character-name">Soldier:</div>
                <div class="character-count">{{ characterCounts.Soldier }}</div>
              </div>
              <div class="character-stat">
                <div class="character-name">Scout:</div>
                <div class="character-count">{{ characterCounts.Scout }}</div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="timeline">
          <h3>Match Timeline</h3>
          <div class="timeline-events">
            <div 
              v-for="(event, index) in sortedEvents" 
              :key="index"
              class="timeline-event"
            >
              <div class="event-time">{{ formatEventTime(event.timeSeconds) }}</div>
              <div class="event-icon" :class="event.type"></div>
              <div class="event-description" v-html="formatEventDescription(event)"></div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Awards Tab -->
      <div v-if="activeTab === 'awards'" class="awards-content">
        <div class="awards-container">
          <div 
            v-for="award in awards" 
            :key="award.title"
            class="award-card"
          >
            <div class="award-icon">🏆</div>
            <div class="award-info">
              <div class="award-title">{{ award.title }}</div>
              <div class="award-recipient">
                <span class="player-name">{{ award.player.username }}</span>
                <span class="team-indicator" :class="award.player.team ? award.player.team.toLowerCase() : 'neutral'" v-if="isTeamMode">
                  ({{ award.player.team }})
                </span>
              </div>
              <div class="award-stat">{{ award.statText }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <div class="actions">
      <button class="action-button" @click="playAgain">Play Again</button>
      <button class="action-button" @click="returnToLobby">Return to Lobby</button>
      <button class="action-button secondary" @click="exitToMainMenu">Main Menu</button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MatchSummary',
  props: {
    matchData: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      activeTab: 'scoreboard',
    };
  },
  computed: {
    isTeamMode() {
      // Check if this is a team-based match (not Free-For-All)
      return this.matchData.redTeamScore !== undefined && this.matchData.blueTeamScore !== undefined;
    },
    matchId() {
      return this.matchData.matchId;
    },
    map() {
      return this.matchData.map;
    },
    matchDuration() {
      return this.matchData.matchDuration;
    },
    redTeamScore() {
      return this.matchData.redTeamScore;
    },
    blueTeamScore() {
      return this.matchData.blueTeamScore;
    },
    winner() {
      return this.matchData.winner;
    },
    currentPlayerId() {
      // This would typically come from the current user's session
      // For now, we'll assume the first player is the current player
      return this.players.length > 0 ? this.players[0].id : '';
    },
    players() {
      return this.matchData.players || [];
    },
    events() {
      return this.matchData.events || [];
    },
    sortedPlayers() {
      return [...this.players].sort((a, b) => this.calculateScore(b) - this.calculateScore(a));
    },
    sortedEvents() {
      return [...this.events].sort((a, b) => a.timeSeconds - b.timeSeconds);
    },
    totalKills() {
      return this.players.reduce((sum, player) => sum + player.kills, 0);
    },
    redTeamKills() {
      if (!this.isTeamMode) return 0;
      return this.players
        .filter(player => player.team === 'Red')
        .reduce((sum, player) => sum + player.kills, 0);
    },
    blueTeamKills() {
      if (!this.isTeamMode) return 0;
      return this.players
        .filter(player => player.team === 'Blue')
        .reduce((sum, player) => sum + player.kills, 0);
    },
    averageKDR() {
      const totalKDR = this.players.reduce((sum, player) => sum + this.calculateKDR(player, true), 0);
      return (totalKDR / this.players.length).toFixed(2);
    },
    redTeamKDR() {
      if (!this.isTeamMode) return 0;
      const redTeam = this.players.filter(player => player.team === 'Red');
      if (redTeam.length === 0) return '0.00';
      const redKDR = redTeam.reduce((sum, player) => sum + this.calculateKDR(player, true), 0);
      return (redKDR / redTeam.length).toFixed(2);
    },
    blueTeamKDR() {
      if (!this.isTeamMode) return 0;
      const blueTeam = this.players.filter(player => player.team === 'Blue');
      if (blueTeam.length === 0) return '0.00';
      const blueKDR = blueTeam.reduce((sum, player) => sum + this.calculateKDR(player, true), 0);
      return (blueKDR / blueTeam.length).toFixed(2);
    },
    characterCounts() {
      const counts = { Soldier: 0, Scout: 0 };
      this.players.forEach(player => {
        if (counts[player.character] !== undefined) {
          counts[player.character]++;
        }
      });
      return counts;
    },
    awards() {
      // Generate awards based on player statistics
      const awards = [];
      
      // MVP Award - highest score
      const mvp = this.sortedPlayers[0];
      if (mvp) {
        awards.push({
          title: 'Match MVP',
          player: mvp,
          statText: `Score: ${this.calculateScore(mvp)}`
        });
      }
      
      // Top Killer - most kills
      const killers = [...this.players].sort((a, b) => b.kills - a.kills);
      if (killers.length > 0 && killers[0].kills > 0) {
        const topKiller = killers[0];
        awards.push({
          title: 'Top Killer',
          player: topKiller,
          statText: `Kills: ${topKiller.kills}`
        });
      }
      
      // Best KD Ratio
      const bestKD = [...this.players].sort((a, b) => 
        this.calculateKDR(b, true) - this.calculateKDR(a, true)
      )[0];
      
      if (bestKD && this.calculateKDR(bestKD) !== '0.00') {
        awards.push({
          title: 'Best K/D Ratio',
          player: bestKD,
          statText: `KDR: ${this.calculateKDR(bestKD)}`
        });
      }
      
      // Top Assist - most assists (if assists tracking is implemented)
      const assistPlayers = [...this.players].filter(p => p.assists && p.assists > 0);
      if (assistPlayers.length > 0) {
        const topAssist = assistPlayers.sort((a, b) => b.assists - a.assists)[0];
        awards.push({
          title: 'Support Master',
          player: topAssist,
          statText: `Assists: ${topAssist.assists}`
        });
      }
      
      return awards;
    }
  },
  methods: {
    calculateScore(player) {
      // Simple scoring formula: 100 points per kill, 50 per assist, -25 per death
      return (player.kills * 100) + (player.assists * 50) - (player.deaths * 25);
    },
    calculateKDR(player, raw = false) {
      const kdr = player.deaths === 0 ? player.kills : player.kills / player.deaths;
      return raw ? kdr : kdr.toFixed(2);
    },
    formatDuration(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
    },
    formatEventTime(seconds) {
      return this.formatDuration(seconds);
    },
    formatEventDescription(event) {
      switch (event.type) {
        case 'start':
        case 'end':
          return event.text;
        case 'kill':
          return `<span class="player-name ${event.killer.team.toLowerCase()}">${event.killer.username}</span> killed <span class="player-name ${event.victim.team.toLowerCase()}">${event.victim.username}</span> with ${event.weapon}`;
        case 'score':
          return `<span class="team-name ${event.team.toLowerCase()}">${event.team} Team</span> scored ${event.points} points (${event.reason})`;
        default:
          return 'Unknown event';
      }
    },
    playAgain() {
      console.log('Playing again...');
      this.$emit('play-again');
    },
    returnToLobby() {
      console.log('Returning to lobby...');
      this.$emit('go-to-lobby');
    },
    exitToMainMenu() {
      console.log('Exiting to main menu...');
      this.$emit('exit-to-main-menu');
    }
  }
};
</script>

<style scoped>
.match-summary {
  max-width: 1200px;
  margin: 0 auto;
  padding: 30px;
  font-family: 'Arial', sans-serif;
}

.summary-header {
  text-align: center;
  margin-bottom: 30px;
}

h1 {
  margin-bottom: 10px;
  color: #333;
}

.match-result {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 20px;
  padding: 10px;
  border-radius: 5px;
}

.match-result.red {
  background: linear-gradient(to right, rgba(255, 0, 0, 0.7), transparent);
}

.match-result.blue {
  background: linear-gradient(to right, rgba(0, 0, 255, 0.7), transparent);
}

.match-result.ffa {
  background: linear-gradient(to right, rgba(128, 0, 128, 0.7), transparent);
}

.match-result.draw {
  background-color: rgba(158, 158, 158, 0.2);
  color: #616161;
}

.match-info {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.match-scores {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}

.team-score {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 20px;
  border-radius: 5px;
}

.team-score.red {
  background-color: rgba(244, 67, 54, 0.1);
}

.team-score.blue {
  background-color: rgba(33, 150, 243, 0.1);
}

.team-name {
  font-size: 14px;
  margin-bottom: 5px;
}

.score {
  font-size: 28px;
  font-weight: bold;
}

.score-separator {
  font-size: 28px;
  font-weight: bold;
  margin: 0 15px;
}

.match-details {
  display: flex;
  gap: 20px;
}

.detail {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.detail-label {
  font-size: 12px;
  color: #666;
}

.detail-value {
  font-weight: bold;
}

.stats-tabs {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
  border-bottom: 1px solid #ddd;
}

.tab-button {
  padding: 12px 25px;
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  font-size: 16px;
  cursor: pointer;
  margin: 0 5px;
  transition: all 0.3s;
}

.tab-button:hover {
  color: #4caf50;
}

.tab-button.active {
  color: #4caf50;
  border-bottom: 3px solid #4caf50;
  font-weight: bold;
}

.tab-content {
  margin-bottom: 30px;
}

/* Scoreboard Tab */
.scoreboard-content {
  overflow-x: auto;
}

.stats-table {
  width: 100%;
  border-collapse: collapse;
}

.stats-table th, .stats-table td {
  padding: 12px 15px;
  text-align: center;
  border-bottom: 1px solid #ddd;
}

.stats-table th {
  background-color: #f5f5f5;
  font-weight: bold;
  color: #333;
}

.player-col {
  text-align: left;
  min-width: 150px;
}

.player-name {
  font-weight: bold;
}

.player-character {
  font-size: 12px;
  color: #666;
  margin-left: 5px;
}

.team-cell {
  font-weight: bold;
}

.team-cell.red {
  color: #ff6b6b;
  font-weight: bold;
}

.team-cell.blue {
  color: #6b9fff;
  font-weight: bold;
}

.team-cell.neutral {
  color: #b0b0b0;
  font-weight: bold;
}

.score-cell {
  font-weight: bold;
}

.stats-table tr.current-player {
  background-color: rgba(76, 175, 80, 0.1);
}

.rank {
  width: 40px;
}

/* Match Details Tab */
.match-stats {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 30px;
}

.stat-card {
  flex: 1;
  background-color: #f5f5f5;
  border-radius: 10px;
  padding: 20px;
  text-align: center;
}

.stat-title {
  font-size: 14px;
  color: #666;
  margin-bottom: 10px;
}

.stat-value {
  font-weight: bold;
  margin-bottom: 15px;
}

.stat-value.large {
  font-size: 32px;
  color: #333;
}

.stat-breakdown {
  display: flex;
  justify-content: center;
  gap: 20px;
}

.team-stat {
  display: flex;
  align-items: center;
  gap: 5px;
}

.team-stat.red .team-name {
  color: #f44336;
}

.team-stat.blue .team-name {
  color: #2196f3;
}

.team-value {
  font-weight: bold;
}

.character-usage {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.character-stat {
  display: flex;
  justify-content: space-between;
  padding: 5px 20px;
}

.character-name {
  font-weight: bold;
}

.timeline {
  background-color: #f5f5f5;
  border-radius: 10px;
  padding: 20px;
}

.timeline h3 {
  text-align: center;
  margin-top: 0;
  margin-bottom: 20px;
  color: #333;
}

.timeline-events {
  max-height: 300px;
  overflow-y: auto;
}

.timeline-event {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 10px 0;
  border-bottom: 1px solid #ddd;
}

.event-time {
  width: 60px;
  font-weight: bold;
  color: #555;
}

.event-icon {
  width: 20px;
  height: 20px;
  border-radius: 50%;
}

.event-icon.kill {
  background-color: #f44336;
}

.event-icon.score {
  background-color: #4caf50;
}

.event-icon.start, .event-icon.end {
  background-color: #2196f3;
}

.event-description {
  flex: 1;
}

/* Awards Tab */
.awards-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
}

.award-card {
  background-color: #f5f5f5;
  border-radius: 10px;
  padding: 20px;
  display: flex;
  gap: 15px;
}

.award-icon {
  font-size: 32px;
}

.award-info {
  flex: 1;
}

.award-title {
  font-weight: bold;
  font-size: 18px;
  margin-bottom: 5px;
}

.award-recipient {
  margin-bottom: 5px;
}

.team-indicator {
  margin-left: 5px;
}

.team-indicator.red {
  color: #ff6b6b;
}

.team-indicator.blue {
  color: #6b9fff;
}

.team-indicator.neutral {
  color: #b0b0b0;
}

.award-stat {
  font-size: 14px;
  color: #666;
}

/* Action buttons */
.actions {
  display: flex;
  justify-content: center;
  gap: 20px;
}

.action-button {
  padding: 12px 25px;
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 5px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s;
}

.action-button:hover {
  background-color: #3e8e41;
}

.action-button.secondary {
  background-color: #f5f5f5;
  color: #333;
}

.action-button.secondary:hover {
  background-color: #e0e0e0;
}

/* Team colors in content */
.red {
  color: #f44336;
}

.blue {
  color: #2196f3;
}
</style> 